import { gql } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { isDefined } from '~utils/types';
import {
  UseConfigContentSource_ConfigFragment,
  UseConfigContentSource_ContentSource_ConfigAppContentSource_Fragment,
  UseConfigContentSource_ContentSource_ConfigBookmarkContentSource_Fragment,
  UseConfigContentSource_ContentSource_ConfigInputContentSource_Fragment,
  UseConfigContentSource_ContentSource_ConfigPlaylistContentSource_Fragment,
  UseConfigContentSource_ContentSourceFragment,
  useConfigUpdateDefaultAppContentSourceMutation,
  useConfigUpdateDefaultBookmarkContentSourceMutation,
  useConfigUpdateDefaultInputContentSourceMutation,
  useConfigUpdateDefaultPlaylistContentSourceMutation,
} from './__generated__/useContentSource.graphql';
import {
  isAppContentSource,
  isBookmarkContentSource,
  isInputContentSource,
  isPlaylistContentSource,
} from './utils';

export const useConfigContentSource = () => {
  const [updateDefaultAppContentSource, { loading: appDefaultContentSourceLoading }] =
    useConfigUpdateDefaultAppContentSourceMutation();
  const [updateDefaultBookmarkContentSource, { loading: bookmarkDefaultContentSourceLoading }] =
    useConfigUpdateDefaultBookmarkContentSourceMutation();
  const [updateDefaultPlaylistContentSource, { loading: playlistDefaultContentSourceLoading }] =
    useConfigUpdateDefaultPlaylistContentSourceMutation();
  const [updateDefaultInputContentSource, { loading: inputDefaultContentSourceLoading }] =
    useConfigUpdateDefaultInputContentSourceMutation();

  const isLoadingDefaultContentSource =
    appDefaultContentSourceLoading ||
    bookmarkDefaultContentSourceLoading ||
    playlistDefaultContentSourceLoading ||
    inputDefaultContentSourceLoading;

  const UpdateDefaultContentSource = useCallback(
    async (
      configuration: UseConfigContentSource_ConfigFragment,
      contentSource: UseConfigContentSource_ContentSourceFragment,
      activity?: string,
    ) => {
      const configurationId = configuration.id;

      switch (contentSource.__typename) {
        case 'ConfigAppContentSource':
          return await updateDefaultAppContentSource({
            variables: buildAppContentSourceInput(configurationId, contentSource, activity),
            refetchQueries: ['CustomerConfigDetailPage'],
            awaitRefetchQueries: true,
          });
        case 'ConfigBookmarkContentSource':
          return await updateDefaultBookmarkContentSource({
            variables: buildBookmarkContentSourceInput(configurationId, contentSource),
            refetchQueries: ['CustomerConfigDetailPage'],
            awaitRefetchQueries: true,
          });
        case 'ConfigInputContentSource':
          return await updateDefaultInputContentSource({
            variables: buildInputContentSourceInput(configurationId, contentSource),
            refetchQueries: ['CustomerConfigDetailPage'],
            awaitRefetchQueries: true,
          });

        case 'ConfigPlaylistContentSource':
          return await updateDefaultPlaylistContentSource({
            variables: buildPlaylistContentSourceInput(configurationId, contentSource),
            refetchQueries: ['CustomerConfigDetailPage'],
            awaitRefetchQueries: true,
          });
      }
    },
    [
      updateDefaultAppContentSource,
      updateDefaultBookmarkContentSource,
      updateDefaultInputContentSource,
      updateDefaultPlaylistContentSource,
    ],
  );

  return {
    isLoadingDefaultContentSource,
    UpdateDefaultContentSource,
  };
};

function buildPlaylistContentSourceInput(
  configurationId: string,
  contentSource: UseConfigContentSource_ContentSource_ConfigPlaylistContentSource_Fragment,
) {
  return {
    input: {
      configurationId,
      playlistId: contentSource.playlistId,
    },
  };
}

function buildAppContentSourceInput(
  configurationId: string,
  contentSource: UseConfigContentSource_ContentSource_ConfigAppContentSource_Fragment,
  activity?: string,
) {
  return {
    input: {
      configurationId,
      applicationId: contentSource.applicationId,
      label: contentSource.label,
      activity: activity,
    },
  };
}

function buildBookmarkContentSourceInput(
  configurationId: string,
  contentSource: UseConfigContentSource_ContentSource_ConfigBookmarkContentSource_Fragment,
) {
  return {
    input: {
      configurationId,
      index: contentSource.index,
    },
  };
}

function buildInputContentSourceInput(
  configurationId: string,
  contentSource: UseConfigContentSource_ContentSource_ConfigInputContentSource_Fragment,
) {
  return {
    input: {
      configurationId,
      source: contentSource.source,
    },
  };
}

export function useContentSourceLabel(
  contentSource: UseConfigContentSource_ContentSourceFragment | undefined,
  config: UseConfigContentSource_ConfigFragment,
) {
  const bookmarkLabel = useMemo(() => {
    if (!isNil(contentSource) && isBookmarkContentSource(contentSource)) {
      const bookmarks = config.bookmarks.all.reported;
      const bookmarkIndex = contentSource.index;

      return bookmarks && isDefined(bookmarkIndex) ? bookmarks[bookmarkIndex] : undefined;
    }

    return undefined;
  }, [config, contentSource]);

  const mediaLabel = useMemo(() => {
    if (!isNil(contentSource) && isPlaylistContentSource(contentSource)) {
      const playlist = config.playlist?.current;
      const isPlaylistActive = isDefined(contentSource.playlistId);

      return playlist && isPlaylistActive ? playlist.title : undefined;
    }

    return undefined;
  }, [config, contentSource]);

  const appLabel = useMemo(() => {
    if (!isNil(contentSource) && isAppContentSource(contentSource)) {
      return contentSource.label || contentSource.applicationId;
    }

    return undefined;
  }, [contentSource]);

  const appActivityLabel = useMemo(() => {
    if (isAppContentSource(contentSource)) {
      return contentSource.selectedActivity || contentSource.label || contentSource.applicationId;
    }
    return undefined;
  }, [contentSource]);

  const headingLabel = useMemo(() => {
    if (isDefined(bookmarkLabel)) {
      return 'Web page';
    }

    if (isDefined(mediaLabel)) {
      return mediaLabel;
    }

    if (isDefined(appLabel)) {
      return appLabel;
    }

    if (isInputContentSource(contentSource)) {
      return contentSource.source;
    }

    return '';
  }, [bookmarkLabel, mediaLabel, appLabel, contentSource]);

  return {
    bookmarkLabel,
    mediaLabel,
    appLabel,
    headingLabel,
    appActivityLabel,
  };
}

useConfigContentSource.graphql = {
  fragments: {
    UseConfigContentSource_contentSource: gql`
      fragment UseConfigContentSource_contentSource on ConfigContentSource {
        ... on ConfigAppContentSource {
          label
          applicationId
          activityList
          selectedActivity
          isBootable
        }
        ... on ConfigBookmarkContentSource {
          index
        }
        ... on ConfigInputContentSource {
          source
        }
        ... on ConfigPlaylistContentSource {
          playlistId
        }
      }
    `,
    UseConfigContentSource_config: gql`
      fragment UseConfigContentSource_config on ConfigurationModel {
        id
        bookmarks {
          all {
            reported
          }
        }
        contentSource {
          available {
            ...UseConfigContentSource_contentSource
          }
          current {
            reported {
              ...UseConfigContentSource_contentSource
            }
          }
          default {
            reported {
              ...UseConfigContentSource_contentSource
            }
          }
        }
        playlist {
          current {
            id
            title
          }
        }
      }
    `,
    UseContentSourceLabel_display: gql`
      fragment UseConfigContentSourceLabel_config on ConfigurationModel {
        id
        ...UseConfigContentSource_config
      }
    `,
  },
  mutations: {
    ConfigUpdateDefaultAppContentSource: gql`
      mutation ConfigUpdateDefaultAppContentSource(
        $input: ConfigurationUpdateDefaultAppContentSourceInput!
      ) {
        configurationUpdateDefaultAppContentSource(input: $input) {
          id
          ...UseConfigContentSource_config
        }
      }
    `,
    ConfigUpdateDefaultBookmarkContent: gql`
      mutation ConfigUpdateDefaultBookmarkContentSource(
        $input: ConfigurationUpdateDefaultBookmarkContentSourceInput!
      ) {
        configurationUpdateDefaultBookmarkContentSource(input: $input) {
          id
          ...UseConfigContentSource_config
        }
      }
    `,
    ConfigUpdateDefaultPlaylistContentSource: gql`
      mutation ConfigUpdateDefaultPlaylistContentSource(
        $input: ConfigurationUpdateDefaultPlaylistContentSourceInput!
      ) {
        configurationUpdateDefaultPlaylistContentSource(input: $input) {
          id
          ...UseConfigContentSource_config
        }
      }
    `,
    ConfigUpdateDefaultInputContentSource: gql`
      mutation ConfigUpdateDefaultInputContentSource(
        $input: ConfigurationUpdateDefaultInputContentSourceInput!
      ) {
        configurationUpdateDefaultInputContentSource(input: $input) {
          id
          ...UseConfigContentSource_config
        }
      }
    `,
  },
};
